import { Box, Button, ButtonProps, SxProps, Tooltip, Typography } from '@mui/material';
import { useGlobalQuery } from '@store/api/global.api.graphql';

import NotificationsNoneIcon from '@mui/icons-material/NotificationsNone';
import { useEffect, useRef, useState } from 'react';
import { Enum_Componentcontentsteamhubnotification_Level } from '@store/gql/graphql';

import { colors } from '@themes/config/theme-colors';
import theme from '@themes/general.theme';
import { hexToRgb } from '@lib/utils/color-utils';

const levelColors: Record<Enum_Componentcontentsteamhubnotification_Level, string> = {
  [Enum_Componentcontentsteamhubnotification_Level.Info]: theme.palette.info.main,
  [Enum_Componentcontentsteamhubnotification_Level.Warning]: theme.palette.warning.main,
  [Enum_Componentcontentsteamhubnotification_Level.Error]: colors.illustrations.red,
  [Enum_Componentcontentsteamhubnotification_Level.Success]: '#1CA543',
};

const buttonColors: Record<Enum_Componentcontentsteamhubnotification_Level, ButtonProps['color']> = {
  [Enum_Componentcontentsteamhubnotification_Level.Info]: 'info',
  [Enum_Componentcontentsteamhubnotification_Level.Warning]: 'warning',
  [Enum_Componentcontentsteamhubnotification_Level.Error]: 'error',
  [Enum_Componentcontentsteamhubnotification_Level.Success]: 'primary',
};

const SteamHubNotification = ({ sx }: { sx?: SxProps }) => {
  const { data: global } = useGlobalQuery();
  const rootRef = useRef<HTMLDivElement>(null);
  const textRef = useRef<HTMLDivElement>(null);

  const [isTextOverflowing, setIsTextOverflowing] = useState(false);

  const notification = global?.global?.data?.attributes?.steamHubNotification;

  const level = notification?.level ?? Enum_Componentcontentsteamhubnotification_Level.Success;
  const color = levelColors[level];
  const rgbColor = hexToRgb(color);
  const buttonColor = buttonColors[level];

  useEffect(() => {
    if (textRef.current) {
      const observer = new ResizeObserver(entries => {
        const element = entries[0].target as HTMLElement;

        const previousOverflowing = window.getComputedStyle(element).overflow;
        const previousOverflowingY = window.getComputedStyle(element).overflowY;
        console.log('previousOverflowing', previousOverflowing);

        // trick to check if text overflows
        element.style.overflow = 'visible';
        element.style.overflowY = 'scroll';

        const isOverflowing = element.scrollHeight > element.clientHeight;
        if (isOverflowing) {
          setIsTextOverflowing(true);
        } else {
          setIsTextOverflowing(false);
        }

        element.style.overflow = previousOverflowing;
        element.style.overflowY = previousOverflowingY;
      });
      observer.observe(textRef.current);

      return () => {
        observer.disconnect();
      };
    }
  }, [notification]);

  if (!notification?.enabled) {
    return null;
  }

  return (
    <Box
      sx={{
        padding: '0px 12px 0px 10px',
        display: 'flex',
        alignItems: 'center',
        gap: '26px',
        border: `1px solid ${color}`,
        borderRadius: '20px',
        background: `linear-gradient(0deg, rgba(28, 165, 67, 0.05) 0%, rgba(${rgbColor.red}, ${rgbColor.green}, ${rgbColor.blue}, 0.05) 100%), #FFF`,
        height: '60px',
        ...sx,
      }}
      ref={rootRef}
    >
      <Box
        sx={{
          borderRadius: '50%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          backgroundColor: color,
          width: '40px',
          height: '40px',
          flexShrink: 0,
          maxWidth: '100%',
        }}
      >
        <NotificationsNoneIcon sx={{ color: '#fff' }} />
      </Box>
      <Box
        sx={{
          flexGrow: 1,
          maxHeight: '100%',
        }}
      >
        <Tooltip
          title={notification?.text}
          arrow
          slotProps={{
            tooltip: {
              sx: {
                fontSize: '1rem',
              },
            },
          }}
          disableInteractive={!isTextOverflowing}
          disableFocusListener={!isTextOverflowing}
          disableHoverListener={!isTextOverflowing}
          disableTouchListener={!isTextOverflowing}
        >
          <Typography
            sx={{
              fontSize: '14px',
              textAlign: 'center',
              // css to limit text to 2 lines and add ellipsis if it overflows
              display: '-webkit-box',
              '-webkit-line-clamp': '2',
              '-webkit-box-orient': 'vertical',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
            }}
            variant={'x-small-bold'}
            component={'div'}
            ref={textRef}
          >
            {notification?.text}
          </Typography>
        </Tooltip>
      </Box>

      <Button
        variant={'contained'}
        sx={{ whiteSpace: 'nowrap', flexShrink: 0 }}
        href={notification.buttonUrl ?? ''}
        target={'_blank'}
        size={'small'}
        color={buttonColor}
      >
        {notification?.buttonText}
      </Button>
    </Box>
  );
};

export default SteamHubNotification;
